import { useState, useMemo, useCallback } from 'react';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import { qNotesByEncounter, type PatientNoteRow } from '@helios/data-access';
import { useVoiceToText, type VttStatus } from '../../../hooks/useVoiceToText';

// ─── Constants ───

const NOTE_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'clinical', label: 'Clinical' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'admin', label: 'Admin' },
] as const;

const VISIBILITY_OPTIONS = [
  { value: 'team', label: 'Team' },
  { value: 'private', label: 'Private' },
  { value: 'front_desk', label: 'Front Desk' },
] as const;

const ALERT_LEVELS = [
  { value: 'none', label: 'None' },
  { value: 'info', label: 'Info', color: '#1d4ed8', bg: '#dbeafe' },
  { value: 'warning', label: 'Warning', color: '#92400e', bg: '#fef3c7' },
  { value: 'critical', label: 'Critical', color: '#dc2626', bg: '#fee2e2' },
] as const;

// ─── Badge helpers ───

function NoteTypeBadge({ type }: { type: string | null }) {
  const label = NOTE_TYPES.find((t) => t.value === type)?.label ?? type ?? 'General';
  return (
    <span
      style={{
        fontSize: 10,
        padding: '1px 8px',
        borderRadius: 'var(--radius-xs)',
        background: 'var(--bg-surface-2, #f5f5f5)',
        color: 'var(--text-muted)',
        fontWeight: 'var(--font-weight-medium)',
      }}
    >
      {label}
    </span>
  );
}

function VisibilityBadge({ visibility }: { visibility: string | null }) {
  const isPrivate = visibility === 'private';
  return (
    <span
      style={{
        fontSize: 10,
        padding: '1px 8px',
        borderRadius: 'var(--radius-xs)',
        background: isPrivate ? '#fef3c7' : 'var(--bg-surface-2, #f5f5f5)',
        color: isPrivate ? '#92400e' : 'var(--text-muted)',
        fontWeight: 'var(--font-weight-medium)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
      }}
    >
      {isPrivate && <NavIcon name="lock" />}
      {VISIBILITY_OPTIONS.find((v) => v.value === visibility)?.label ?? visibility ?? 'Team'}
    </span>
  );
}

function AlertBadge({ level }: { level: string | null }) {
  if (!level || level === 'none') return null;
  const cfg = ALERT_LEVELS.find((a) => a.value === level);
  if (!cfg || !('color' in cfg)) return null;
  return (
    <span
      style={{
        fontSize: 10,
        padding: '1px 8px',
        borderRadius: 'var(--radius-xs)',
        background: cfg.bg,
        color: cfg.color,
        fontWeight: 'var(--font-weight-semi)',
      }}
    >
      {cfg.label}
    </span>
  );
}

// ─── Helpers ───

function formatNoteDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── Mic button ───

const MIC_BTN_SIZE = 32;
const RING_R = 13; // radius of progress ring
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_R;

function MicButton({
  status,
  primary,
  elapsed,
  maxDuration,
  onStart,
  onStop,
  onReset,
}: {
  status: VttStatus;
  primary: string;
  elapsed: number;
  maxDuration: number;
  onStart: () => void;
  onStop: () => void;
  onReset: () => void;
}) {
  const isRecording = status === 'recording';
  const isTranscribing = status === 'transcribing';
  const isError = status === 'error';

  const progress = isRecording ? Math.min(elapsed / maxDuration, 1) : 0;
  const dashOffset = RING_CIRCUMFERENCE * (1 - progress);

  const remainingSec = Math.max(0, maxDuration - elapsed);
  const mm = String(Math.floor(remainingSec / 60));
  const ss = String(remainingSec % 60).padStart(2, '0');

  return (
    <button
      type="button"
      title={
        isRecording
          ? `Stop recording (${mm}:${ss} left)`
          : isError
            ? 'Retry'
            : isTranscribing
              ? 'Transcribing...'
              : 'Start dictation'
      }
      disabled={isTranscribing}
      onClick={() => {
        if (isRecording) onStop();
        else if (isError) onReset();
        else onStart();
      }}
      style={{
        position: 'absolute',
        top: 8,
        right: 8,
        width: MIC_BTN_SIZE,
        height: MIC_BTN_SIZE,
        borderRadius: '50%',
        border: 'none',
        background: isRecording ? '#dc2626' : isError ? '#fef2f2' : 'transparent',
        color: isRecording ? '#fff' : isError ? '#dc2626' : primary,
        cursor: isTranscribing ? 'wait' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        opacity: isTranscribing ? 0.4 : 1,
      }}
    >
      {/* Progress ring overlay during recording */}
      {isRecording && (
        <svg
          width={MIC_BTN_SIZE}
          height={MIC_BTN_SIZE}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            transform: 'rotate(-90deg)',
            pointerEvents: 'none',
          }}
        >
          {/* Background track */}
          <circle
            cx={MIC_BTN_SIZE / 2}
            cy={MIC_BTN_SIZE / 2}
            r={RING_R}
            fill="none"
            stroke="rgba(255,255,255,0.3)"
            strokeWidth={3}
          />
          {/* Progress arc */}
          <circle
            cx={MIC_BTN_SIZE / 2}
            cy={MIC_BTN_SIZE / 2}
            r={RING_R}
            fill="none"
            stroke="#fff"
            strokeWidth={3}
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            style={{ transition: 'stroke-dashoffset 1s linear' }}
          />
        </svg>
      )}
      <NavIcon name={isRecording ? 'mic-off' : 'mic'} />
    </button>
  );
}

// ─── Props ───

interface EncounterNotesTabProps {
  patientId: string;
  encounterId: string;
  visitId?: string | null;
  /** When true, hide the internal "Notes" title + subtitle (used in Guided Note where the section header already labels the section) */
  hideHeader?: boolean;
  /** When true, use a quiet icon-only add button instead of a prominent CTA. Empty state gets the primary action instead. */
  quietActions?: boolean;
}

// ─── Component ───

export function EncounterNotesTab({
  patientId,
  encounterId,
  visitId,
  hideHeader,
  quietActions,
}: EncounterNotesTabProps) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const userName = user?.displayName ?? user?.email ?? '';

  const { data: noteRows = [] } = usePowerSyncQuery<PatientNoteRow>(qNotesByEncounter(encounterId));

  // Best-effort private filtering
  const visibleNotes = useMemo(
    () => noteRows.filter((n) => n.visibility !== 'private' || n.author_user_id === userId),
    [noteRows, userId],
  );

  // ── Composer state ──
  const [composerOpen, setComposerOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [visibility, setVisibility] = useState('team');
  const [alertLevel, setAlertLevel] = useState('none');
  const [saving, setSaving] = useState(false);

  // ── Voice-to-text ──
  const vttEnabled = Boolean(import.meta.env.VITE_VOICE_TO_TEXT_URL);
  // Notes defaults to translation mode (PRD.177 §5.5) — non-English dictation produces English output
  const vtt = useVoiceToText('translation', (text) => {
    setBody((prev) => (prev ? prev.trimEnd() + '\n' + text : text));
  });

  const resetComposer = useCallback(() => {
    setTitle('');
    setBody('');
    setNoteType('general');
    setVisibility('team');
    setAlertLevel('none');
    setComposerOpen(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!db || !body.trim()) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      await db.execute(
        `INSERT INTO patient_notes
          (id, org_id, clinic_id, patient_id, encounter_id, visit_id, title, body, note_type, visibility, alert_level, author_user_id, author_name, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          crypto.randomUUID(),
          orgId,
          null,
          patientId,
          encounterId,
          visitId ?? null,
          title.trim() || 'Note',
          body.trim(),
          noteType,
          visibility,
          alertLevel,
          userId,
          userName,
          now,
          now,
        ],
      );
      resetComposer();
    } finally {
      setSaving(false);
    }
  }, [
    db,
    orgId,
    patientId,
    encounterId,
    visitId,
    title,
    body,
    noteType,
    visibility,
    alertLevel,
    userId,
    userName,
    resetComposer,
  ]);

  const handleDelete = useCallback(
    async (id: string) => {
      if (!db) return;
      await db.execute(`DELETE FROM patient_notes WHERE id = ?`, [id]);
    },
    [db],
  );

  const selectStyle = {
    height: 32,
    padding: '0 8px',
    fontSize: 'var(--font-size-xs)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-surface)',
    color: 'var(--text)',
  };

  return (
    <div
      style={{
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {!hideHeader && (
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Notes
            </h2>
            <p
              style={{
                margin: '4px 0 0',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
              }}
            >
              Clinical notes for this encounter
            </p>
          </div>
        )}
        {hideHeader && <div />}
        {!composerOpen &&
          (quietActions ? (
            <button
              onClick={() => setComposerOpen(true)}
              title="Add Note"
              style={{
                width: 30,
                height: 30,
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              <NavIcon name="plus" />
            </button>
          ) : (
            <button
              onClick={() => setComposerOpen(true)}
              style={{
                height: 36,
                padding: '0 16px',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: primary,
                color: '#fff',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <NavIcon name="plus" /> Add Note
            </button>
          ))}
      </div>

      {/* Composer */}
      {composerOpen && (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--sp-4)',
            background: 'var(--bg-surface)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3)',
          }}
        >
          {/* Selectors row */}
          <div
            style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', alignItems: 'center' }}
          >
            <select
              value={noteType}
              onChange={(e) => setNoteType(e.target.value)}
              style={selectStyle}
            >
              {NOTE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              style={selectStyle}
            >
              {VISIBILITY_OPTIONS.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </select>
            <select
              value={alertLevel}
              onChange={(e) => setAlertLevel(e.target.value)}
              style={selectStyle}
            >
              {ALERT_LEVELS.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
          </div>

          {/* Title */}
          <input
            type="text"
            placeholder="Title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{
              height: 34,
              padding: '0 12px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text)',
              outline: 'none',
            }}
          />

          {/* Body + mic */}
          <div style={{ position: 'relative' }}>
            <textarea
              placeholder="Write your note..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '10px 12px',
                paddingRight: vttEnabled ? 40 : 12,
                borderRadius: 'var(--radius-sm)',
                border: `1px solid ${vtt.status === 'recording' ? primary : 'var(--border)'}`,
                background: 'var(--bg-surface)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text)',
                resize: 'vertical',
                fontFamily: 'inherit',
                outline: 'none',
                lineHeight: 'var(--line-height-body)',
              }}
            />
            {vttEnabled && (
              <MicButton
                status={vtt.status}
                primary={primary}
                elapsed={vtt.elapsed}
                maxDuration={vtt.maxDuration}
                onStart={() => vtt.start()}
                onStop={() => vtt.stop()}
                onReset={() => vtt.reset()}
              />
            )}
          </div>
          {/* VTT status indicator */}
          {vtt.status === 'transcribing' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  animation: 'spin 1s linear infinite',
                }}
              >
                <NavIcon name="loader" />
              </span>
              Transcribing...
            </div>
          )}
          {vtt.status === 'error' && vtt.error && (
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: '#dc2626',
              }}
            >
              {vtt.error}
            </div>
          )}
          {vtt.lastRecordingUrl && vtt.status !== 'recording' && (
            <button
              type="button"
              onClick={() => vtt.playLastRecording()}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                fontSize: 'var(--font-size-xs)',
                color: primary,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <NavIcon name="activity" /> Play last recording
            </button>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <button
              onClick={resetComposer}
              style={{
                height: 32,
                padding: '0 14px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !body.trim()}
              style={{
                height: 32,
                padding: '0 14px',
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                background: !body.trim() ? 'var(--text-disabled)' : primary,
                color: '#fff',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                cursor: !body.trim() ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Saving...' : 'Save Note'}
            </button>
          </div>
        </div>
      )}

      {/* Notes list */}
      {visibleNotes.length === 0 && !composerOpen ? (
        <div
          style={{
            textAlign: 'center',
            padding: 'var(--sp-8)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
            border: '2px dashed var(--border)',
            borderRadius: 'var(--radius-lg)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--sp-3)',
          }}
        >
          <span>No notes for this encounter yet.</span>
          {quietActions && (
            <button
              onClick={() => setComposerOpen(true)}
              style={{
                height: 32,
                padding: '0 14px',
                borderRadius: 'var(--radius-md)',
                border: `1px solid ${primary}`,
                background: 'transparent',
                color: primary,
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <NavIcon name="plus" /> Add Note
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          {visibleNotes.map((note) => {
            const isWarningOrCritical =
              note.alert_level === 'warning' || note.alert_level === 'critical';
            const alertCfg = ALERT_LEVELS.find(
              (a) => a.value === note.alert_level && 'color' in a,
            ) as { color: string; bg: string } | undefined;
            return (
              <div
                key={note.id}
                style={{
                  border: isWarningOrCritical
                    ? `1px solid ${alertCfg?.color ?? 'var(--border)'}`
                    : '1px solid var(--border)',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--sp-4)',
                  background: isWarningOrCritical
                    ? (alertCfg?.bg ?? 'var(--bg-surface)')
                    : 'var(--bg-surface)',
                }}
              >
                {/* Header: badges + meta */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 8,
                  }}
                >
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <NoteTypeBadge type={note.note_type} />
                    <VisibilityBadge visibility={note.visibility} />
                    <AlertBadge level={note.alert_level} />
                  </div>
                  {/* Delete button */}
                  {note.author_user_id === userId && (
                    <button
                      onClick={() => handleDelete(note.id)}
                      style={{
                        width: 24,
                        height: 24,
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: 'var(--text-placeholder)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <NavIcon name="x" />
                    </button>
                  )}
                </div>

                {/* Title */}
                {note.title && note.title !== 'Note' && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 'var(--font-weight-semi)',
                      color: 'var(--text)',
                      marginBottom: 4,
                    }}
                  >
                    {note.title}
                  </div>
                )}

                {/* Body */}
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text)',
                    lineHeight: 'var(--line-height-body)',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {note.body}
                </div>

                {/* Footer: author + timestamp */}
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--sp-3)',
                    marginTop: 8,
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {note.author_name && <span>{note.author_name}</span>}
                  <span>{formatNoteDate(note.created_at)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
